<template>
    <div>
        <DCard padding>
            <DRow class="mt-2 mb-2">
                <DCol
                    class="pt-1"
                >
                    <h4>{{ __('customers.invoices.group_name') }}: {{ items.groupName }}</h4>
                </DCol>
            </DRow>
            <hr>
            <DRow
                class="mt-2 mb-3"
                align-vertical="end"
            >
                <DCol
                    sm="5"
                    class="pt-1"
                >
                    <DInput
                        id="search"
                        v-model="filterData.search"
                        :label="__('messages.search')"
                        :placeholder="__('customers.invoices.search_placeholder')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        :label="__('messages.actions.filter')"
                        @click="filter(items.current_page, filterData)"
                    />
                    <DButton
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters(filterData)"
                    />
                </DCol>
            </DRow>
            <DRow>
                <DCol>
                    <DTable
                        :items="items.items"
                        :fields="fields"
                        :header="true"
                    >
                        <template #tax_rate="{item}">
                            <td>{{ item.tax_rate + '%' }}</td>
                        </template>
                        <template #price="{item}">
                            <td>{{ roundPrice(item.price) }}</td>
                        </template>
                        <template #net_amount="{item}">
                            <td>{{ roundPrice(item.net_amount) }}</td>
                        </template>
                    </DTable>
                    <DPagination
                        v-if="items.last_page > 1"
                        :active-page="items.current_page"
                        :pages="items.last_page"
                        align="center"
                        @update:activePage="changePage"
                    />
                </DCol>
            </DRow>
        </DCard>
        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
        </DFabs>
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <UpdateAccountingProductGroupModal
            v-if="showUpdateAccountingProductModal"
            v-model:showModal="showUpdateAccountingProductModal"
            :item="updateAccountingProductItem"
            :accounting-product-options="accountingProducts"
            :customer-id="customer.id"
            :invoice-id="invoice.id"
            :errors="errors"
            :group-name="items.groupName"
        />
        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
            <DFab
                v-if="showUpdateAccountingProductButton(items.items[0])"
                id="btn-update"
                icon="pencil"
                :label="__('customers.invoices.update_accounting_products', {name: items.groupName})"
                @click="showUpdateAccountingProductModalForItem(items.items[0])"
            />
        </DFabs>
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import _ from 'lodash'
import UpdateAccountingProductGroupModal from '~@/Components/Customers/Invoices/UpdateAccountingProductGroupModal'

export default {
    name: 'InvoiceItemsList',
    components: { UpdateAccountingProductGroupModal, CreateCloudPitCustomerModal, SSOFab },
    layout: [App, CustomerBase],
    props: {
        invoice: {
            type: Object,
            default () {
                return {}
            }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        items: {
            type: Object,
            default: () => { return {} }
        },
        filters: {
            type: Object,
            default: () => { return {} }
        },
        accountingProducts: {
            type: Object,
            default: () => ({})
        },
        errors: {
            type: Object,
            default: () => ({})
        }
    },
    data () {
        return {
            fields: [
                { key: 'contract_id', label: this.__('customers.invoices.contract_id') },
                { key: 'line_item', label: this.__('customers.invoices.form.line_item') },
                { key: 'description', label: this.__('customers.invoices.form.description') },
                { key: 'tax_rate', label: this.__('customers.invoices.tax_rate') },
                { key: 'from_date', label: this.__('customers.invoices.form.from_date') },
                { key: 'to_date', label: this.__('customers.invoices.form.to_date') },
                { key: 'price', label: this.__('customers.invoices.form.price') },
                { key: 'quantity', label: this.__('customers.invoices.form.quantity') },
                { key: 'net_amount', label: this.__('customers.invoices.net_amount') }
            ],
            showCreateCloudpitCustomerModal: false,
            filterData: _.cloneDeep(this.filters),
            showUpdateAccountingProductModal: false,
            updateAccountingProductItem: {}
        }
    },
    computed: {
        invoiceDecimals () {
            return this.$page.props.marketRegions[this.customer.market_region_id]?.decimal_places ?? 2
        }
    },
    methods: {
        changePage (page) {
            this.filter(page, this.filterData)
        },
        filter (page, filters) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            if (page > 1) {
                queryParameters.page = page
            }

            this.invoice.type.startsWith('invoice')
                ? this.$inertia.get(this.$route('customers.customers.invoices.items-list', { customer: this.customer.id, invoice: this.invoice.id }), queryParameters)
                : this.$inertia.get(this.$route('customers.customers.credit-notes.items-list', { customer: this.customer.id, credit_note: this.invoice.id }), queryParameters)
        },
        resetFilters (filters) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            queryParameters.search = null

            this.invoice.type.startsWith('invoice')
                ? this.$inertia.get(this.$route('customers.customers.invoices.items-list', { customer: this.customer.id, invoice: this.invoice.id }), queryParameters)
                : this.$inertia.get(this.$route('customers.customers.credit-notes.items-list', { customer: this.customer.id, credit_note: this.invoice.id }), queryParameters)
        },
        roundPrice (value) {
            if (value || value === 0.0) {
                return this.priceFormat(value, this.invoiceDecimals, this.customer.market_region_id)
            }

            return ''
        },
        showUpdateAccountingProductModalForItem (item) {
            this.updateAccountingProductItem = item
            this.showUpdateAccountingProductModal = true
        },
        showUpdateAccountingProductButton (item) {
            return item.has_default_accounting_product &&
                this.invoice.finance_system_synced_at === null
        }
    }
}
</script>
